
import { computed, defineComponent, ref } from 'vue'
import { organizationMembersFilter, useOrganizationModule } from '@/modules/organizations'
import { MemberProfile } from '@/modules/members/types'
import { useRoute, useRouter } from 'vue-router'
import useQuasar from 'quasar/src/composables/use-quasar.js';
import { MembersRouteNames, OrganizationsRouteNames } from '@/router/routes-names'
import { useAPIPagination } from '@/views/_shared/search-list/pagination'
import ConfirmDeletionDialog from '@/components/_shared/dialogs/confirm-deletion-dialog.vue'
import ShowUpdateMembersResponse from '@/views/organizations/dialogs/show-update-members-response.vue'
import ResourceLink from '@/components/_shared/links/resource-link.vue'
import { truncate } from 'lodash'

export default defineComponent({
    name: 'view-organizations-organization-members',
    components: { ResourceLink },
    async setup () {
        const $q = useQuasar()
        const {
            getOrganizationMembers,
            organizationMembers,
            organizationMembersCount,
            deleteMemberFromOrganization,
            getOrganizationsSimplified,
            organizationsSimplified,
            updateMembersProfiles
        } = useOrganizationModule()
        const router = useRouter()
        const route = useRoute()

        const organizationId = route.params.organizationId as string

        const organizationMemberColumns = ([
            { name: 'memberId', label: 'Id', field: 'memberId', sortable: true, align: 'left' },
            { name: 'email', label: 'Email', field: 'email', sortable: true, align: 'left' },
            { name: 'firstName', label: 'First Name', field: 'firstName', sortable: true, align: 'left' },
            { name: 'lastName', label: 'Last Name', field: 'lastName', sortable: true, align: 'left' },
            { name: 'agreements', label: 'Agreement(s)', field: 'agreements', align: 'left' },
            { name: 'supervisor', label: 'Supervisor', field: (row: MemberProfile) => row.supervisor ? row.supervisor.firstName + ' ' + row.supervisor?.lastName : '', sortable: true, align: 'left' },
            { name: 'actions', label: 'Actions', field: 'actions', align: 'left' }
        ])

        const filter = ref('')
        const currentPage = computed(() => route.query.page === null ? '1' : route.query.page)

        const { loading, pagination, onRequest } = useAPIPagination(
            organizationMembersFilter,
            getOrganizationMembers,
            organizationMembersCount,
            currentPage,
            true,
            '',
            organizationId
        )

        function onShowMember (props: MemberProfile) {
            router.push({
                name: OrganizationsRouteNames.MEMBERS_SHOW,
                params: { organizationId: organizationId, memberId: props.memberId },
                query: { page: route.query.page }
            })
        }

        function onAddMember () {
            router.push({
                name: OrganizationsRouteNames.MEMBERS_NEW,
                params: { organizationId: organizationId },
                query: { page: route.query.page }
            })
        }

        async function onDeleteMember (memberRow: MemberProfile) {
            $q.dialog({
                component: ConfirmDeletionDialog,
                componentProps: {
                    entityType: 'member',
                    identifier: `"${memberRow.firstName} ${memberRow.lastName}"`,
                    deleteCallback: async function () {
                        await deleteMemberFromOrganization(memberRow.memberId, organizationId, {
                            memberId: memberRow.memberId,
                            organizationId: organizationId
                        })
                    }
                }
            })
        }

        function onShowMemberHistory () {
            // TODO: show member history
        }

        function onShowMemberOrders () {
            // TODO: show member orders
        }

        await getOrganizationMembers(organizationId as string)
        await getOrganizationsSimplified()

        await onRequest({
            filter: '',
            pagination: pagination.value
        })

        const currentOrganization = organizationsSimplified.value.find(organization => organization.id === organizationId)
        const organizationsListRoute = { name: OrganizationsRouteNames.LIST, query: { page: route.query.page } }

        const csvFile = ref<File | null>(null)
        const canUpload = computed(() => csvFile.value !== null)

        function onRejected () {
            $q.notify({
                type: 'negative',
                message: 'File did not pass validation constraints'
            })
        }

        async function uploadFile () {
            const formData:any = new FormData()
            if (csvFile.value !== null) {
                formData.append('csvFile', csvFile.value)

                try {
                    const response = await updateMembersProfiles(organizationId, formData)
                    csvFile.value = null
                    if (response.length > 0) {
                        return $q.dialog({
                            component: ShowUpdateMembersResponse,
                            componentProps: {
                                response: response
                            },
                            cancel: true,
                            persistent: true
                        })
                    }
                } catch (error) {
                    $q.notify({
                        color: 'negative',
                        message: error.toString()
                    })
                }
            }
        }

        async function updateMembers () {
            $q.dialog({
                title: 'Update members',
                message: 'Are you sure you want to update members profiles?',
                cancel: true,
                persistent: true
            }).onOk(() => {
                uploadFile()
                $q.notify({
                    color: 'positive',
                    message: 'Members profiles updated'
                })
            })
        }

        const currentOrganizationRoute = computed(() => {
            return {
                name: OrganizationsRouteNames.SHOW,
                query: { page: route.query.page },
                params: { organizationId: route.params.organizationId }
            }
        })

        const agreementResources = (memberRow: MemberProfile) => {
            return memberRow.agreements.map((agreement) => {
                const name = truncate(agreement.name, { length: 40 })
                return {
                    type: 'agreement',
                    id: agreement.id,
                    alias: agreement.id,
                    text: name
                }
            })
        }

        return {
            route,
            organizationMembers,
            organizationMemberColumns,
            onShowMember,
            onAddMember,
            onDeleteMember,
            onShowMemberHistory,
            onShowMemberOrders,
            OrganizationsRouteNames,
            MembersRouteNames,
            currentOrganization,
            organizationsListRoute,
            loading,
            pagination,
            onRequest,
            filter,
            csvFile,
            onRejected,
            canUpload,
            uploadFile,
            updateMembers,
            agreementResources,
            currentOrganizationRoute
        }
    }
})
